import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Garage, GarageExit, Direction } from '../lib/types';
import { computeCompassData } from '../lib/exitScoring';
import { DIRECTIONS } from '../lib/constants';

export interface VenueDirScore {
  direction: Direction;
  score: number; // 0-10 scale (use scoreTo100 for display)
  label: string;
  exitName: string;
}

export interface FeaturedVenue {
  id: string;
  name: string;
  slug: string;
  city: string;
  state: string;
  image_url: string | null;
  venue_type: string;
  scores: Record<Direction, VenueDirScore>;
}

interface VenueGarageRow {
  garage: (Garage & { exits: GarageExit[] }) | null;
}

interface VenueRow {
  id: string;
  name: string;
  slug: string;
  venue_type: string;
  image_url: string | null;
  cities: { name: string; state: string } | null;
  venue_garages: VenueGarageRow[];
}

/**
 * Fetch a handful of venues that have garages with scored exits, and compute a
 * venue-level best-per-direction compass (0-10) from their garages' exits using
 * the canonical clearance scoring. Real data only.
 */
export function useFeaturedVenues(limit = 4) {
  const [venues, setVenues] = useState<FeaturedVenue[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);

      // Pull a pool of venues with their linked garages + exits. We over-fetch so
      // we can filter to venues that actually have scored vehicle exits.
      const { data } = await supabase
        .from('venues')
        .select(
          'id, name, slug, venue_type, image_url, cities(name, state), venue_garages(garage:garages(*, exits:garage_exits(*)))',
        )
        .order('capacity', { ascending: false })
        .limit(40);

      const rows = (data as unknown as VenueRow[] | null) ?? [];

      const built: FeaturedVenue[] = [];
      for (const row of rows) {
        const allExits: GarageExit[] = [];
        for (const vg of row.venue_garages ?? []) {
          if (vg.garage?.exits) allExits.push(...vg.garage.exits);
        }
        const vehicleExits = allExits.filter((e) => e.exit_type !== 'pedestrian');
        if (vehicleExits.length === 0) continue;

        const compass = computeCompassData(allExits);
        // Require at least 2 directions with a real score so cards aren't mostly empty
        const realDirs = compass.filter((c) => c.score > 0).length;
        if (realDirs < 2) continue;

        const scores = {} as Record<Direction, VenueDirScore>;
        for (const dir of DIRECTIONS) {
          const c = compass.find((x) => x.direction === dir)!;
          scores[dir] = {
            direction: dir,
            score: c.score,
            label: c.label,
            exitName: c.exitName,
          };
        }

        built.push({
          id: row.id,
          name: row.name,
          slug: row.slug,
          venue_type: row.venue_type,
          image_url: row.image_url ?? null,
          city: row.cities?.name ?? '',
          state: row.cities?.state ?? '',
          scores,
        });

        if (built.length >= limit) break;
      }

      if (!cancelled) {
        setVenues(built);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { venues, loading };
}
